import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type PaymentOptionManagementRecord = {
  payment_option_management_id: string;
  client_id: string;
  statuses: string[];
  payment_items: string[];
  created_at: string;
  updated_at?: string;
};

export const fetchPaymentOptionManagement =
  createAsyncThunk<PaymentOptionManagementRecord | null>(
    "paymentOptionManagement/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await ipponApi.get(
          `${BaseURL}/api/v1/payment-option-management`
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        return list.length > 0
          ? (list[0] as PaymentOptionManagementRecord)
          : null;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data || "Failed to fetch payment option management"
        );
      }
    }
  );

export const createPaymentOptionManagement = createAsyncThunk<
  PaymentOptionManagementRecord,
  { statuses?: string[]; payment_items?: string[] }
>(
  "paymentOptionManagement/create",
  async (body, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/payment-option-management`,
        body
      );
      return res.data.data as PaymentOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          "Failed to create payment option management"
      );
    }
  }
);

export const updatePaymentOptionManagement = createAsyncThunk<
  PaymentOptionManagementRecord,
  {
    payment_option_management_id: string;
    statuses?: string[];
    payment_items?: string[];
  }
>(
  "paymentOptionManagement/update",
  async ({ payment_option_management_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/payment-option-management/${payment_option_management_id}`,
        body
      );
      return res.data.data as PaymentOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          "Failed to update payment option management"
      );
    }
  }
);

export const upsertPaymentOptionManagement = createAsyncThunk<
  PaymentOptionManagementRecord,
  {
    existingId?: string | null;
    statuses?: string[];
    payment_items?: string[];
  }
>(
  "paymentOptionManagement/upsert",
  async ({ existingId, ...body }, { dispatch, rejectWithValue }) => {
    try {
      if (existingId) {
        const resultAction: any = await dispatch(
          updatePaymentOptionManagement({
            payment_option_management_id: existingId,
            ...body,
          })
        );
        if (updatePaymentOptionManagement.rejected.match(resultAction)) {
          throw resultAction.payload || new Error("update failed");
        }
        return resultAction.payload as PaymentOptionManagementRecord;
      }
      const resultAction: any = await dispatch(
        createPaymentOptionManagement(body)
      );
      if (createPaymentOptionManagement.rejected.match(resultAction)) {
        throw resultAction.payload || new Error("create failed");
      }
      return resultAction.payload as PaymentOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

type PaymentOptionManagementState = {
  loading: boolean;
  error: boolean;
  record: PaymentOptionManagementRecord | null;
};

const initialState: PaymentOptionManagementState = {
  loading: false,
  error: false,
  record: null,
};

export const paymentOptionManagementSlice = createSlice({
  name: "paymentOptionManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentOptionManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchPaymentOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(fetchPaymentOptionManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(createPaymentOptionManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(createPaymentOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(createPaymentOptionManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(updatePaymentOptionManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(updatePaymentOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(updatePaymentOptionManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(upsertPaymentOptionManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(upsertPaymentOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(upsertPaymentOptionManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export default paymentOptionManagementSlice.reducer;
